import type {
  TableDispatchGroupDto,
  TableDispatchApiResponseDto,
  TableDispatchExportRequestDto,
  TableDispatchMetricDto,
  TableDispatchRequestDto,
  TableDispatchResultDto,
} from './table-dispatch.api.types';
import type {
  TableDispatchGroup,
  TableDispatchApiResponse,
  TableDispatchMetric,
  TableDispatchRequestInput,
  TableDispatchResult,
  TableDispatchSortBy,
} from './table-dispatch.types';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 50;
const DEFAULT_SORT_BY = 'sent';
const DEFAULT_SORT_ORDER = 'desc';

const SORT_BY_TO_API: Record<TableDispatchSortBy, TableDispatchRequestDto['sort_by']> = {
  feedbackCount: 'feedback_count',
  participationRate: 'participation_rate',
  sent: 'sent',
  value: 'value',
};

export function toTableDispatchExportRequestDto(input: TableDispatchRequestInput): TableDispatchExportRequestDto {
  const groupByValues = input.schemaSelections.reduce<Record<string, string[]>>(
    (valuesByField, { fieldCode, values }) => {
      const normalizedValues = values?.map((value) => value.trim()).filter(Boolean);

      if (normalizedValues?.length) {
        valuesByField[fieldCode] = [...new Set(normalizedValues)];
      }

      return valuesByField;
    },
    {},
  );

  return {
    campaign_type: input.campaignType,
    channel_ids: input.channelId,
    ...(input.dispatchFieldFilters?.length && {
      dispatch_field_filters: input.dispatchFieldFilters.map(({ fieldCode, operator, value }) => ({
        field_code: fieldCode,
        operator,
        value,
      })),
    }),
    exclude_blank_breakouts: input.excludeBlankBreakouts ?? true,
    flow_id: input.flowId,
    from_date: input.fromDate,
    group_by: input.schemaSelections.map(({ fieldCode }) => `dispatch_field:${fieldCode}`),
    ...(Object.keys(groupByValues).length > 0 && { group_by_values: groupByValues }),
    include_overall: input.includeOverall ?? true,
    nodes: input.nodeId,
    to_date: input.toDate,
  };
}

export function toTableDispatchRequestDto(input: TableDispatchRequestInput): TableDispatchRequestDto {
  const searchString = input.searchString?.trim();

  return {
    ...toTableDispatchExportRequestDto(input),
    page: input.page ?? DEFAULT_PAGE,
    per_page: input.perPage ?? DEFAULT_PER_PAGE,
    ...(searchString && { search_string: searchString }),
    sort_by: SORT_BY_TO_API[input.sortBy ?? DEFAULT_SORT_BY],
    sort_order: input.sortOrder ?? DEFAULT_SORT_ORDER,
  };
}

export function fromTableDispatchMetricDto(dto: TableDispatchMetricDto): TableDispatchMetric {
  return {
    feedbackCount: dto.feedback_count,
    participationRate: dto.participation_rate,
    sent: dto.sent,
  };
}

export function fromTableDispatchGroupDto(dto: TableDispatchGroupDto): TableDispatchGroup {
  return {
    fieldCode: dto.field_code,
    fieldName: dto.field_name,
    hasMore: dto.has_more,
    pagination: {
      current: dto.pagination.current,
      next: dto.pagination.next,
      perPage: dto.pagination.per_page,
      previous: dto.pagination.previous,
      totalCount: dto.pagination.total_count,
      totalPages: dto.pagination.total_pages,
    },
    rows: dto.rows.map((row) => ({
      ...fromTableDispatchMetricDto(row),
      value: row.value,
    })),
  };
}

export function fromTableDispatchResultDto(dto: TableDispatchResultDto): TableDispatchResult {
  return {
    groups: dto.groups.map(fromTableDispatchGroupDto),
    ...(dto.overall && { overall: fromTableDispatchMetricDto(dto.overall) }),
  };
}

export function normalizeTableDispatchApiResponse(response: TableDispatchApiResponseDto): TableDispatchApiResponse {
  if ('job_id' in response) {
    return { jobId: response.job_id };
  }

  return { data: fromTableDispatchResultDto(response.data) };
}
